/**
 * System Prompt Builder
 *
 * Assembles agent system prompts from 5 standardized sections.
 * Ensures consistency across all agents while allowing customization.
 *
 * ## The 5 Mandatory Sections
 *
 * 1. **Intro** - Who is this agent? What does it do? What are its goals?
 * 2. **Tools** - What tools are available? How to use each one?
 * 3. **Suggestions** - Best practices, tips, common patterns
 * 4. **Context Note** - How context IDs work, artifact references
 * 5. **Evaluation** - What is performance measured against? Final reminders
 *
 * ## Usage
 *
 * ```typescript
 * const builder = new SystemPromptBuilder();
 *
 * const prompt = builder.build({
 *     intro: ORCHESTRATOR_INTRO,
 *     tools: (params) => buildToolsSection(params.tools),
 *     suggestions: ORCHESTRATOR_SUGGESTIONS,
 *     contextNote: SHARED_CONTEXT_NOTE,
 *     evaluation: ORCHESTRATOR_EVALUATION,
 * }, { tools: this.toolCalls });
 * ```
 */
import { DEFAULT_SEPARATOR, DEFAULT_SECTION_HEADERS, } from './types';
// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT BUILDER CLASS
// ═══════════════════════════════════════════════════════════════════════════
export class SystemPromptBuilder {
    constructor(options = {}) {
        this.options = {
            separator: options.separator ?? DEFAULT_SEPARATOR,
            includeSectionHeaders: options.includeSectionHeaders ?? false,
            sectionHeaders: {
                ...DEFAULT_SECTION_HEADERS,
                ...options.sectionHeaders,
            },
        };
    }
    /**
     * Build the complete system prompt from sections
     *
     * @param sections - The 5 mandatory sections (can be strings or functions)
     * @param params - Parameters passed to section functions
     * @returns The assembled system prompt string
     */
    build(sections, params) {
        const resolvedSections = [
            this.resolveSection('intro', sections.intro, params),
            this.resolveSection('tools', sections.tools, params),
            this.resolveSection('suggestions', sections.suggestions, params),
            this.resolveSection('contextNote', sections.contextNote, params),
            this.resolveSection('evaluation', sections.evaluation, params),
        ];
        return resolvedSections.join(this.options.separator);
    }
    /**
     * Static helper to build a prompt with default options
     */
    static build(options) {
        const builder = new SystemPromptBuilder({
            includeSectionHeaders: options.includeSectionHeaders,
            separator: options.separator,
        });
        return builder.build(options.sections, options.params);
    }
    /**
     * Resolve a section to a string
     */
    resolveSection(sectionName, content, params) {
        // Resolve content (string or function)
        const resolved = typeof content === 'function'
            ? content(params)
            : content;
        // Add section header if enabled
        if (this.options.includeSectionHeaders) {
            const header = this.options.sectionHeaders[sectionName];
            return `## ${header}\n\n${resolved}`;
        }
        return resolved;
    }
    // ═══════════════════════════════════════════════════════════════════════
    // TOOL SECTION HELPERS
    // ═══════════════════════════════════════════════════════════════════════
    /**
     * Build the tools section from an array of tools
     * Uses each tool's systemPromptUsage property
     *
     * @param tools - Array of tools to document
     * @param options - Formatting options
     */
    static buildToolsSection(tools, options = {}) {
        const { includeExamples = true, includeMistakes = true, header = '## Available Tools and How To Use Them', } = options;
        const lines = [header, ''];
        for (const tool of tools) {
            const usage = tool.description?.systemPromptUsage;
            const name = tool.name;
            lines.push(`### ${name}`);
            // Brief description
            if (usage?.briefDescription) {
                lines.push(usage.briefDescription);
            }
            else if (tool.description?.function?.description) {
                lines.push(tool.description.function.description);
            }
            lines.push('');
            // When to use
            if (usage?.whenToUse && usage.whenToUse.length > 0) {
                lines.push('**When to use:**');
                for (const when of usage.whenToUse) {
                    lines.push(`- ${when}`);
                }
                lines.push('');
            }
            // When NOT to use
            if (usage?.whenNotToUse && usage.whenNotToUse.length > 0) {
                lines.push('**When NOT to use:**');
                for (const when of usage.whenNotToUse) {
                    lines.push(`- ❌ ${when}`);
                }
                lines.push('');
            }
            // Capabilities (for agents-as-tools)
            if (usage?.capabilities && usage.capabilities.length > 0) {
                lines.push('**Capabilities:**');
                for (const cap of usage.capabilities) {
                    lines.push(`- ✓ ${cap}`);
                }
                lines.push('');
            }
            // Limitations (for agents-as-tools)
            if (usage?.limitations && usage.limitations.length > 0) {
                lines.push('**Limitations:**');
                for (const lim of usage.limitations) {
                    lines.push(`- ⚠️ ${lim}`);
                }
                lines.push('');
            }
            // Critical rules
            if (usage?.criticalRules && usage.criticalRules.length > 0) {
                lines.push('**🚨 Critical Rules:**');
                for (const rule of usage.criticalRules) {
                    lines.push(`- ${rule}`);
                }
                lines.push('');
            }
            // Workflow
            if (usage?.workflow) {
                if (usage.workflow.before && usage.workflow.before.length > 0) {
                    lines.push('**Before using:**');
                    for (const step of usage.workflow.before) {
                        lines.push(`1. ${step}`);
                    }
                    lines.push('');
                }
                if (usage.workflow.after && usage.workflow.after.length > 0) {
                    lines.push('**After using:**');
                    for (const step of usage.workflow.after) {
                        lines.push(`1. ${step}`);
                    }
                    lines.push('');
                }
            }
            // Example usage
            if (includeExamples && usage?.exampleUsage) {
                lines.push('**Example:**');
                lines.push('```json');
                lines.push(usage.exampleUsage);
                lines.push('```');
                lines.push('');
            }
            // Common mistakes
            if (includeMistakes && usage?.commonMistakes && usage.commonMistakes.length > 0) {
                lines.push('**Common mistakes:**');
                for (const mistake of usage.commonMistakes) {
                    lines.push(`- ❌ ${mistake}`);
                }
                lines.push('');
            }
        }
        return lines.join('\n');
    }
    /**
     * Build a single tool's documentation
     */
    static buildToolDoc(tool) {
        return SystemPromptBuilder.buildToolsSection([tool], {
            header: `### ${tool.name}`,
            includeExamples: true,
            includeMistakes: true,
        });
    }
}
// ═══════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════
export { SystemPromptBuilder as default };
